import React, { useMemo } from 'react';
import {
    Box,
    Paper,
    Typography,
    ToggleButton,
    ToggleButtonGroup,
    useTheme,
    Tooltip as MuiTooltip,
} from '@mui/material';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Legend,
    AreaChart,
    Area,
    PieChart,
    Pie,
    Cell,
} from 'recharts';
import type { TymoveGrafyResponse } from '../api/JsdhNotifikaceService/generated/models/TymoveGrafy';

export type TeamChartView = 'clenove' | 'heatmapa' | 'reakce' | 'typyHlavni' | 'typyDetail' | 'topRespondenti';

interface TeamTrendChartProps {
    data: TymoveGrafyResponse;
    allowedViews?: TeamChartView[];
}

type ChartView = TeamChartView;

const MEDAL_COLORS = ['#FFD700', '#C0C0C0', '#CD7F32']; // Gold, Silver, Bronze
const PIE_COLORS = ['#f44336', '#2196f3', '#4caf50', '#ff9800', '#9c27b0', '#00bcd4', '#795548', '#607d8b'];

const ALL_VIEWS: ChartView[] = ['clenove', 'heatmapa', 'reakce', 'typyHlavni', 'typyDetail', 'topRespondenti'];

const TeamTrendChart: React.FC<TeamTrendChartProps> = ({ data, allowedViews }) => {
    const theme = useTheme();
    const effectiveViews = allowedViews ?? ALL_VIEWS;
    const [view, setView] = React.useState<ChartView>(effectiveViews[0] ?? 'clenove');

    // Data pro graf porovnani clenu
    const clenoveData = useMemo(() => {
        if (!data.porovnaniClenu) return [];
        return data.porovnaniClenu.map((clen) => ({
            jmeno: clen.jmeno,
            pocetJedu: clen.pocetJedu,
            pocetNejedu: clen.pocetNejedu,
            prumernaDobaReakce: clen.prumernaDobaReakce ? Math.round(clen.prumernaDobaReakce) : null,
        }));
    }, [data.porovnaniClenu]);

    // Data pro heatmapu aktivity
    const heatmapaData = useMemo(() => {
        if (!data.heatmapaAktivity) return { matrix: [], maxValue: 0 };

        // Create 7x24 matrix (days x hours)
        const matrix: number[][] = Array(7).fill(null).map(() => Array(24).fill(0));
        let maxValue = 0;

        data.heatmapaAktivity.forEach((item) => {
            const dayIndex = item.denCislo - 1; // 1-7 to 0-6
            const hourIndex = item.hodina;
            if (dayIndex >= 0 && dayIndex < 7 && hourIndex >= 0 && hourIndex < 24) {
                matrix[dayIndex][hourIndex] = item.pocetOdpovedi;
                maxValue = Math.max(maxValue, item.pocetOdpovedi);
            }
        });

        return { matrix, maxValue };
    }, [data.heatmapaAktivity]);

    // Data pro trend reakce tymu
    const reakceData = useMemo(() => {
        if (!data.trendReakce) return [];
        return data.trendReakce.map((item) => ({
            tyden: `T${item.tyden}`,
            datum: item.datum,
            prumerna: item.prumernaDobaReakce ? Math.round(item.prumernaDobaReakce) : null,
            min: item.minDobaReakce ? Math.round(item.minDobaReakce) : null,
            max: item.maxDobaReakce ? Math.round(item.maxDobaReakce) : null,
        }));
    }, [data.trendReakce]);

    // Data pro statistiky podle hlavního typu (pred)
    const typyHlavniData = useMemo(() => {
        if (!data.statistikyPodleTypu) return [];
        return data.statistikyPodleTypu.map((item) => ({
            typ: item.typUdalosti || 'Neznámý',
            pocetPoplachu: item.pocetPoplachu,
            pocetJedu: item.pocetJedu,
            pocetNejedu: item.pocetNejedu,
            procentoJedu: item.procentoJedu ? Math.round(item.procentoJedu) : 0,
            prumernaDobaReakce: item.prumernaDobaReakce ? Math.round(item.prumernaDobaReakce) : null,
        }));
    }, [data.statistikyPodleTypu]);

    // Data pro koláčový graf podle hlavního typu
    const typyPieData = useMemo(() => {
        if (!data.statistikyPodleTypu) return [];
        const total = data.statistikyPodleTypu.reduce((sum, item) => sum + item.pocetPoplachu, 0);
        return data.statistikyPodleTypu.map((item, index) => ({
            name: item.typUdalosti || 'Neznámý',
            value: item.pocetPoplachu,
            percent: total > 0 ? Math.round((item.pocetPoplachu / total) * 100) : 0,
            color: PIE_COLORS[index % PIE_COLORS.length],
        }));
    }, [data.statistikyPodleTypu]);

    // Data pro statistiky podle typu a podtypu
    const typyDetailData = useMemo(() => {
        if (!data.statistikyPodleTypuPodtypu) return [];
        return data.statistikyPodleTypuPodtypu.map((item) => ({
            nazev: `${item.typ} - ${item.podtyp}`,
            typ: item.typ,
            podtyp: item.podtyp,
            pocetPoplachu: item.pocetPoplachu,
            pocetJedu: item.pocetJedu,
            pocetNejedu: item.pocetNejedu,
            procentoJedu: item.procentoJedu ? Math.round(item.procentoJedu) : 0,
            prumernaDobaReakce: item.prumernaDobaReakce ? Math.round(item.prumernaDobaReakce) : null,
        }));
    }, [data.statistikyPodleTypuPodtypu]);

    // Data pro top respondenty
    const topRespondentiData = useMemo(() => {
        const topRespondenti = data.topRespondenti;
        if (!topRespondenti) return [];

        // Group by month
        const byMonth = new Map<string, typeof topRespondenti>();
        topRespondenti.forEach((item) => {
            const existing = byMonth.get(item.mesic) || [];
            existing.push(item);
            byMonth.set(item.mesic, existing);
        });

        return Array.from(byMonth.entries())
            .sort((a, b) => a[0].localeCompare(b[0]))
            .slice(-6) // Last 6 months
            .map(([mesic, respondenti]) => ({
                mesic: formatMonth(mesic),
                respondenti: respondenti.sort((a, b) => a.poradi - b.poradi),
            }));
    }, [data]);

    const handleViewChange = (
        _event: React.MouseEvent<HTMLElement>,
        newView: ChartView | null
    ) => {
        if (newView) {
            setView(newView);
        }
    };

    const noData = !data.porovnaniClenu || data.porovnaniClenu.length === 0;
    const dny = ['Po', 'Út', 'St', 'Čt', 'Pá', 'So', 'Ne'];

    const getHeatmapColor = (value: number, maxValue: number) => {
        if (value === 0 || maxValue === 0) return theme.palette.action.hover;
        const intensity = value / maxValue;
        const r = Math.round(33 + (76 - 33) * (1 - intensity));
        const g = Math.round(150 + (175 - 150) * (1 - intensity));
        const b = Math.round(243 + (80 - 243) * (1 - intensity));
        return `rgb(${r}, ${g}, ${b})`;
    };

    const viewTitles: Record<ChartView, string> = {
        clenove: 'Porovnání členů',
        heatmapa: 'Heatmapa aktivity',
        reakce: 'Trend doby reakce týmu',
        typyHlavni: 'Poplachy dle typu',
        typyDetail: 'Statistiky podle typu a podtypu',
        topRespondenti: 'Top respondenti v čase',
    };

    return (
        <Paper sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
                <Typography variant="h6">
                    {viewTitles[view]}
                </Typography>

                <ToggleButtonGroup
                    value={view}
                    exclusive
                    onChange={handleViewChange}
                    size="small"
                    sx={{ flexWrap: 'wrap' }}
                >
                    {effectiveViews.includes('clenove') && (
                        <ToggleButton value="clenove">Členové</ToggleButton>
                    )}
                    {effectiveViews.includes('heatmapa') && (
                        <ToggleButton value="heatmapa" disabled={!data.heatmapaAktivity?.length}>
                            Heatmapa
                        </ToggleButton>
                    )}
                    {effectiveViews.includes('reakce') && (
                        <ToggleButton value="reakce" disabled={!data.trendReakce?.length}>
                            Reakce
                        </ToggleButton>
                    )}
                    {effectiveViews.includes('typyHlavni') && (
                        <ToggleButton value="typyHlavni" disabled={!data.statistikyPodleTypu?.length}>
                            Typy
                        </ToggleButton>
                    )}
                    {effectiveViews.includes('typyDetail') && (
                        <ToggleButton value="typyDetail" disabled={!data.statistikyPodleTypuPodtypu?.length}>
                            Detail
                        </ToggleButton>
                    )}
                    {effectiveViews.includes('topRespondenti') && (
                        <ToggleButton value="topRespondenti" disabled={!data.topRespondenti?.length}>
                            Top
                        </ToggleButton>
                    )}
                </ToggleButtonGroup>
            </Box>

            {noData ? (
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    height={300}
                    sx={{ opacity: 0.5 }}
                >
                    <Typography color="text.secondary">
                        Žádná data k zobrazení
                    </Typography>
                </Box>
            ) : (
                <Box height={view === 'heatmapa' ? 350 : 300}>
                    {/* Graf porovnani clenu */}
                    {view === 'clenove' && clenoveData.length > 0 && (
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={clenoveData} layout="vertical">
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    type="number"
                                    fontSize={12}
                                    tick={{ fill: theme.palette.text.secondary }}
                                />
                                <YAxis
                                    type="category"
                                    dataKey="jmeno"
                                    fontSize={12}
                                    width={100}
                                    tick={{ fill: theme.palette.text.secondary }}
                                />
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: theme.palette.background.paper,
                                        borderColor: theme.palette.divider,
                                    }}
                                    formatter={(value, name) => {
                                        if (name === 'Reakce (s)') return value ? `${value}s` : 'N/A';
                                        return value;
                                    }}
                                />
                                <Legend />
                                <Bar dataKey="pocetJedu" name="JEDU" stackId="a" fill={theme.palette.success.main} />
                                <Bar dataKey="pocetNejedu" name="NEJEDU" stackId="a" fill={theme.palette.error.main} />
                            </BarChart>
                        </ResponsiveContainer>
                    )}

                    {/* Heatmapa aktivity */}
                    {view === 'heatmapa' && heatmapaData.matrix.length > 0 && (
                        <Box sx={{ overflowX: 'auto' }}>
                            <Box sx={{ minWidth: 600 }}>
                                {/* Header - hodiny */}
                                <Box display="flex" mb={0.5}>
                                    <Box width={40} />
                                    {Array.from({ length: 24 }, (_, h) => (
                                        <Box
                                            key={h}
                                            width={24}
                                            textAlign="center"
                                            fontSize={10}
                                            color="text.secondary"
                                        >
                                            {h}
                                        </Box>
                                    ))}
                                </Box>
                                {/* Rows - dny */}
                                {heatmapaData.matrix.map((row, dayIndex) => (
                                    <Box key={dayIndex} display="flex" alignItems="center" mb={0.5}>
                                        <Box width={40} fontSize={12} color="text.secondary">
                                            {dny[dayIndex]}
                                        </Box>
                                        {row.map((value, hourIndex) => (
                                            <MuiTooltip
                                                key={hourIndex}
                                                title={`${dny[dayIndex]} ${hourIndex}:00 - ${value} odpovědí`}
                                                arrow
                                            >
                                                <Box
                                                    width={24}
                                                    height={24}
                                                    sx={{
                                                        backgroundColor: getHeatmapColor(value, heatmapaData.maxValue),
                                                        borderRadius: 0.5,
                                                        cursor: 'pointer',
                                                        '&:hover': {
                                                            transform: 'scale(1.1)',
                                                        },
                                                    }}
                                                />
                                            </MuiTooltip>
                                        ))}
                                    </Box>
                                ))}
                                {/* Legenda */}
                                <Box display="flex" alignItems="center" mt={2} gap={1}>
                                    <Typography variant="caption" color="text.secondary">
                                        Min
                                    </Typography>
                                    <Box
                                        width={100}
                                        height={12}
                                        sx={{
                                            background: `linear-gradient(to right, ${theme.palette.action.hover}, ${theme.palette.primary.main})`,
                                            borderRadius: 0.5,
                                        }}
                                    />
                                    <Typography variant="caption" color="text.secondary">
                                        Max ({heatmapaData.maxValue})
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    )}

                    {/* Trend doby reakce */}
                    {view === 'reakce' && reakceData.length > 0 && (
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={reakceData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    dataKey="tyden"
                                    fontSize={12}
                                    tick={{ fill: theme.palette.text.secondary }}
                                />
                                <YAxis
                                    fontSize={12}
                                    tick={{ fill: theme.palette.text.secondary }}
                                    label={{ value: 's', angle: -90, position: 'insideLeft' }}
                                />
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: theme.palette.background.paper,
                                        borderColor: theme.palette.divider,
                                    }}
                                    formatter={(value) => value ? `${value}s` : 'N/A'}
                                    labelFormatter={(label, payload) => {
                                        if (payload?.[0]?.payload?.datum) {
                                            return `Týden od ${formatDate(payload[0].payload.datum)}`;
                                        }
                                        return label;
                                    }}
                                />
                                <Legend />
                                <Area
                                    type="monotone"
                                    dataKey="max"
                                    name="Max"
                                    stroke={theme.palette.error.light}
                                    fill={theme.palette.error.light}
                                    fillOpacity={0.2}
                                />
                                <Area
                                    type="monotone"
                                    dataKey="prumerna"
                                    name="Průměr"
                                    stroke={theme.palette.primary.main}
                                    fill={theme.palette.primary.main}
                                    fillOpacity={0.4}
                                />
                                <Area
                                    type="monotone"
                                    dataKey="min"
                                    name="Min"
                                    stroke={theme.palette.success.main}
                                    fill={theme.palette.success.main}
                                    fillOpacity={0.6}
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    )}

                    {/* Statistiky podle hlavního typu (pred) - koláčový graf */}
                    {view === 'typyHlavni' && typyPieData.length > 0 && (
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={typyPieData}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={60}
                                    outerRadius={100}
                                    paddingAngle={2}
                                    dataKey="value"
                                    label={({ name, percent }) => `${name} ${percent}%`}
                                    labelLine={{ stroke: theme.palette.text.secondary }}
                                >
                                    {typyPieData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: theme.palette.background.paper,
                                        borderColor: theme.palette.divider,
                                    }}
                                    formatter={(value, name) => [`${value} poplachů`, name]}
                                />
                            </PieChart>
                        </ResponsiveContainer>
                    )}

                    {/* Statistiky podle typu a podtypu */}
                    {view === 'typyDetail' && typyDetailData.length > 0 && (
                        <Box sx={{ overflowY: 'auto', height: '100%' }}>
                            {typyDetailData.map((item, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 2,
                                        p: 1,
                                        borderBottom: `1px solid ${theme.palette.divider}`,
                                        '&:hover': { bgcolor: theme.palette.action.hover },
                                    }}
                                >
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="body2" fontWeight="medium" noWrap>
                                            {item.typ}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" noWrap>
                                            {item.podtyp}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ textAlign: 'center', minWidth: 50 }}>
                                        <Typography variant="body2" fontWeight="bold">
                                            {item.pocetPoplachu}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            poplachů
                                        </Typography>
                                    </Box>
                                    <Box sx={{ textAlign: 'center', minWidth: 50 }}>
                                        <Typography variant="body2" color="success.main" fontWeight="bold">
                                            {item.pocetJedu}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            JEDU
                                        </Typography>
                                    </Box>
                                    <Box sx={{ textAlign: 'center', minWidth: 50 }}>
                                        <Typography variant="body2" color="error.main" fontWeight="bold">
                                            {item.pocetNejedu}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            NEJEDU
                                        </Typography>
                                    </Box>
                                    <Box sx={{ textAlign: 'center', minWidth: 50 }}>
                                        <Typography variant="body2" fontWeight="bold">
                                            {item.procentoJedu}%
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            účast
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    )}

                    {/* Top respondenti */}
                    {view === 'topRespondenti' && topRespondentiData.length > 0 && (
                        <Box sx={{ overflowX: 'auto', height: '100%' }}>
                            <Box display="flex" gap={2} height="100%" minWidth={topRespondentiData.length * 150}>
                                {topRespondentiData.map((month) => (
                                    <Box key={month.mesic} flex={1} minWidth={120}>
                                        <Typography
                                            variant="subtitle2"
                                            textAlign="center"
                                            mb={1}
                                            color="text.secondary"
                                        >
                                            {month.mesic}
                                        </Typography>
                                        {month.respondenti.map((resp, idx) => (
                                            <Box
                                                key={resp.uzivatelId}
                                                display="flex"
                                                alignItems="center"
                                                gap={1}
                                                mb={1}
                                                p={1}
                                                sx={{
                                                    backgroundColor: theme.palette.action.hover,
                                                    borderRadius: 1,
                                                    borderLeft: `3px solid ${MEDAL_COLORS[idx] || theme.palette.divider}`,
                                                }}
                                            >
                                                <Typography variant="h6" sx={{ color: MEDAL_COLORS[idx] || 'inherit' }}>
                                                    {idx + 1}.
                                                </Typography>
                                                <Box flex={1}>
                                                    <Typography variant="body2" noWrap>
                                                        {resp.jmeno}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {resp.pocetJedu} JEDU
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    )}
                </Box>
            )}
        </Paper>
    );
};

function formatMonth(mesic: string): string {
    const [year, month] = mesic.split('-');
    const monthNames = ['Led', 'Úno', 'Bře', 'Dub', 'Kvě', 'Čer', 'Čvc', 'Srp', 'Zář', 'Říj', 'Lis', 'Pro'];
    const monthIndex = parseInt(month, 10) - 1;
    return `${monthNames[monthIndex]} ${year.slice(-2)}`;
}

function formatDate(dateStr: string): string {
    const date = new Date(dateStr);
    return `${date.getDate()}.${date.getMonth() + 1}.`;
}

export default React.memo(TeamTrendChart);
